import { useVirtualizer, useWindowVirtualizer, type VirtualItem } from '@tanstack/vue-virtual';
import type { MaybeRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, toValue, watch } from 'vue';

export type VirtualizationSettings<T> = {
  // In 'page' mode the whole page is used as scroll container
  // In 'parent' mode the direct parent of the table is used as scroll container (useful for table with its own internal scroll)
  // Ref, getter, or element can be also provided as link to scroll container on other level
  mode: 'page' | 'parent' | MaybeRefOrGetter<HTMLElement>;
  // A getter function to retrieve estimated row height. Use when row height differs from default or dynamic.
  // When row height is expected to be dynamic specify the largest height that is knowingly possible for the row.
  rowHeightGetter: (item: T, index: number) => number;
  // Set to true if rows are expected to be of indefinite height.
  dynamicRows?: boolean;
  // Number of rows to render before/after viewBox
  overscan?: number;
};

export const useMaybeVirtualizedRows = <T>(
  data: Ref<T[]>,
  rowKeys: Ref<string[]>,
  settings: MaybeRef<VirtualizationSettings<T>> | undefined,
  rowsContainerRef: Ref<HTMLElement | null | undefined>,
  scrollChildRef?: Ref<HTMLTableElement | undefined>,
) => {
  const windowVirtualizerOptions = computed(() => {
    const unwrappedSettings = settings === undefined ? undefined : toValue(settings);
    if (unwrappedSettings?.mode !== 'page') return { count: 0, estimateSize: () => 0 };

    return {
      count: data.value.length,
      estimateSize: (index: number) => unwrappedSettings.rowHeightGetter(data.value[index], index),
      overscan: unwrappedSettings.overscan ?? 20,
      getItemKey: (index: number) => rowKeys.value[index],
    };
  });
  const containerVirtualizerOptions = computed(() => {
    const unwrappedSettings = settings === undefined ? undefined : toValue(settings);
    const mode = unwrappedSettings?.mode;
    if (unwrappedSettings === undefined || mode === undefined || mode === 'page') {
      return {
        count: 0,
        estimateSize: () => 0,
        getScrollElement: () => new HTMLDivElement(),
        overscan: 0,
      };
    }

    return {
      getScrollElement: () => {
        if (mode === 'parent') {
          return scrollChildRef?.value?.parentElement ?? null;
        }

        return toValue(mode);
      },
      count: data.value.length,
      estimateSize: (index: number) => unwrappedSettings.rowHeightGetter(data.value[index], index),
      overscan: unwrappedSettings.overscan,
      getItemKey: (index: number) => rowKeys.value[index],
    };
  });

  const virtualizer = (() => {
    const unwrappedSettings = settings === undefined ? undefined : toValue(settings);

    if (unwrappedSettings === undefined) return undefined;
    // Virtualizer that uses page scroll
    if (unwrappedSettings?.mode === 'page') return useWindowVirtualizer(windowVirtualizerOptions);
    // Virtualizer that uses custom container or table body for scroll
    return useVirtualizer(containerVirtualizerOptions);
  })();

  // Consolidated iterable set of rows
  const virtualizedOrNotRows = computed((): VirtualItem[] => {
    if (virtualizer?.value !== undefined) {
      return virtualizer.value.getVirtualItems();
    }

    return data.value.map((_, index) => ({
      index,
      key: rowKeys.value[index],
      start: 0,
      end: 0,
      size: 0,
      lane: 0,
    }));
  });

  // Offsets that are used to fill empty (virtualized) area.
  const virtualOffsetTop = computed(() => virtualizedOrNotRows.value[0]?.start ?? 0);
  const virtualOffsetBottom = computed(() => {
    if (virtualizer?.value === undefined) return 0;

    return (
      virtualizer.value.getTotalSize() -
      (virtualizedOrNotRows.value[virtualizedOrNotRows.value.length - 1]?.end ?? 0)
    );
  });
  const virtualizedTotalSize = computed(() => virtualizer?.value?.getTotalSize() ?? 0);

  const measureElement = (element: Element | null) => {
    const unwrappedSettings = settings === undefined ? undefined : toValue(settings);
    if (unwrappedSettings?.dynamicRows === true) {
      requestAnimationFrame(() => virtualizer?.value?.measureElement(element));
    }
  };

  const hasKeysOrderBeenChanged = (nextKeys: string[], prevKeys: string[]) => {
    if (nextKeys.length !== prevKeys.length) return true;

    for (let i = 0; i < nextKeys.length; i++) {
      if (nextKeys[i] !== prevKeys[i]) return true;
    }

    return false;
  };

  // When data is changed we need to make sure we show correct set of rows -> remove measurement cache
  watch(rowKeys, (nextKeys, prevKeys) => {
    if (hasKeysOrderBeenChanged(nextKeys, prevKeys)) {
      virtualizer?.value?.measure();
    }
  });

  return {
    measureElement,
    virtualOffsetTop,
    virtualOffsetBottom,
    virtualizedOrNotRows,
    virtualizedTotalSize,
  };
};
